import { z } from 'zod';
import { IsoDatetime, LangCode } from './primitives.js';

const Id = z.string().max(40);

// The house graph (01 §2, decision 11): everything the household talks
// about is an entity with a stable id — "Rada" is not a name in a message,
// it is an entity with history.
export const EntityKind = z.enum([
  'person',
  'pet',
  'vehicle',
  'home',
  'room',
  'institution',
  'vendor',
  'recipe',
  'key',
  'document',
  'medication',
]);
export type EntityKind = z.infer<typeof EntityKind>;

export const EntityState = z.enum(['active', 'inactive', 'archived']);
export type EntityState = z.infer<typeof EntityState>;

export const Entity = z.object({
  id: Id,
  household_id: Id,
  kind: EntityKind,
  name: z.string().min(1),
  state: EntityState.default('active'),
  // Per-kind attributes bag, validated by Zod here — not in the DB.
  attributes: z.record(z.any()).default({}),
  // Set when the entity is also an authenticated user; SET NULL — the dog
  // survives the login.
  user_id: Id.nullable(),
  // Set when the entity is also staff in the existing execution layer.
  staff_id: Id.nullable(),
  // A column, because this is the thing you tap to dial.
  phone: z.string().nullable(),
  // Native language for staff (S9); register per context (S7).
  preferred_lang: LangCode.nullable(),
  archived_at: IsoDatetime.nullable(),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});
export type Entity = z.infer<typeof Entity>;

// Graph edges are TEMPORAL (01 §2.2): when Rada is replaced, the edge is
// ended, not overwritten. valid_until NULL = currently valid; an edge with
// valid_until in the past is history, not current truth.
export const EntityRelationKind = z.enum([
  'works_for',
  'responsible_for',
  'preferred_by',
  'lives_in',
  'owner_of',
  'authorized_pickup',
  'custody_of',
  'member_of',
  'covers_for',
  'family_of',
]);
export type EntityRelationKind = z.infer<typeof EntityRelationKind>;

export const EntityRelation = z
  .object({
    id: Id,
    household_id: Id,
    from_entity_id: Id,
    to_entity_id: Id,
    kind: EntityRelationKind,
    valid_from: IsoDatetime,
    valid_until: IsoDatetime.nullable(),
    created_at: IsoDatetime,
  })
  .refine(
    (relation) => relation.valid_until === null || relation.valid_until > relation.valid_from,
    'valid_until must be after valid_from',
  );
export type EntityRelation = z.infer<typeof EntityRelation>;

export const TemporalFactSource = z.enum([
  'extraction_job',
  'owner_confirmed',
  'manual',
  'blueprint',
]);
export type TemporalFactSource = z.infer<typeof TemporalFactSource>;

// The factual memory with validity (01 §2.3, 02 §6.3): every fact is a
// triple with valid_from/valid_until. Retiring a fact sets valid_until —
// never deletes; a new fact supersedes rather than overwrites ("Rada"
// supersedes "Karim"). NOTE: this is the structured memory the engine
// queries; the prose rule-book stays in memory.ts (memory_entries).
export const TemporalFact = z
  .object({
    id: Id,
    household_id: Id,
    subject_entity_id: Id,
    predicate: z.string().min(1),
    // The object: free text or an entity reference — both, because "my
    // waiter = Rada" is a name AND an entity.
    object_text: z.string().nullable(),
    object_entity_id: Id.nullable(),
    valid_from: IsoDatetime,
    valid_until: IsoDatetime.nullable(),
    source: TemporalFactSource,
    source_message_id: Id.nullable(),
    // Only confident facts enter automatically (mem0-style); the rest wait
    // for confirmation.
    confidence: z.number().int().min(0).max(100).default(100),
    // Core block (Letta-style): the ~20 always-useful facts of the
    // household, always in context.
    core_block: z.boolean().default(false),
    supersedes_fact_id: Id.nullable(),
    created_at: IsoDatetime,
  })
  .refine(
    (fact) => fact.valid_until === null || fact.valid_until > fact.valid_from,
    'valid_until must be after valid_from',
  )
  .refine(
    (fact) => fact.object_text !== null || fact.object_entity_id !== null,
    'a fact needs an object: object_text or object_entity_id',
  );
export type TemporalFact = z.infer<typeof TemporalFact>;
